import { CurrencyPipe, DatePipe, NgClass } from '@angular/common';
import { Component, inject, OnInit, signal, ChangeDetectionStrategy, input, linkedSignal } from '@angular/core';
import { MatDivider } from "@angular/material/divider";
import { MatProgressSpinner } from "@angular/material/progress-spinner";
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatTab, MatTabGroup } from '@angular/material/tabs';
import { catchError, finalize, forkJoin, of } from 'rxjs';
import { PrmsOrderLine } from '../../model/prms-order-line';
import { PrmsOrderShipment } from '../../model/prms-order-shipment';
import { PrmsOrderSummary } from '../../model/prms-order-summary';
import { PrmsOrdersSearchService } from '../../services/prms-order-search-service';
import { FileUtils } from '../../utils/file-utils';
import { StringUtils } from '../../utils/string-utils';

@Component({
  selector: 'app-prms-order-search-details',
  templateUrl: './orders-search-details-dialog.component.html',
  styleUrls: ['./orders-search-details-dialog.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatTableModule, MatTabGroup, MatTab, DatePipe, MatDivider, CurrencyPipe, MatProgressSpinner]
})

export class PrmsOrdersSearchDetailsComponent implements OnInit {

  private readonly service = inject(PrmsOrdersSearchService);
  readonly prmsOrderSummaryInput = input.required<PrmsOrderSummary>({ alias: 'prmsOrderSummary' });
  readonly prmsOrderSummary = linkedSignal(() => this.prmsOrderSummaryInput());
  isBusy = signal(false);
  errorMessage = signal<string | null>(null);
  shipmentsErrorMessage = signal<string | null>(null);
  detailsErrorMessage = signal<string | null>(null);
  orderLineColumns: string[] = ['lineNumber', 'productNumber', 'productDescription', 'unitPrice',
    'quantityOrdered', 'quantityShipped', 'status', 'requestedDate', 'scheduledDate', 'promiseDate'];
  shipmentLineColumns: string[] = ['lineNumber', 'productNumber', 'productDescription', 'invoiceNumber',
    'quantityOrdered', 'quantityShipped', 'shipDate', 'shipVia', 'trackingNumber', 'completeFlag', 'download'];
  orderLineDataSource = new MatTableDataSource<PrmsOrderLine>([]);
  shipmentsDataSource = new MatTableDataSource<PrmsOrderShipment>([]);
  invoiceErrorMessage = signal<string | null>(null);

  ngOnInit(): void {
    this.isBusy.set(true);
    this.errorMessage.set(null);
    this.isBusy.set(true);

    forkJoin([
      this.service.getOrderDetails(this.prmsOrderSummary().orderNumber).pipe(catchError(async err => {
        this.detailsErrorMessage.set("Error loading order details: " + await StringUtils.getErrorMessageAsync(err));
        this.orderLineDataSource.data = [];
        return null;
      })),
      this.service.getOrderShipments(this.prmsOrderSummary().orderNumber).pipe(catchError(async err => {
        this.shipmentsErrorMessage.set("Error loading shipments: " + await StringUtils.getErrorMessageAsync(err));
        this.shipmentsDataSource.data = [];
        return null;
      }))
    ]).pipe(finalize(() => this.isBusy.set(false))).subscribe(([details, shipments]) => {
      if (details) {
        this.prmsOrderSummary.set(details);
        this.orderLineDataSource.data = details.lines;
      }
      if (shipments) {
        this.shipmentsDataSource.data = shipments;
        shipments.forEach(shipment => this.bolExists(shipment))
      }
    });
  }

  public downloadInvoice(shipment: PrmsOrderShipment): void {
    const invoiceNumber = shipment.invoiceNumber;
    const orderNumber = this.prmsOrderSummary().orderNumber;
    shipment.invoiceDownloading = true;
    this.service.getInvoiceDocument(orderNumber, invoiceNumber).pipe(
      finalize(() => shipment.invoiceDownloading = false)).subscribe({
        next: (result: Blob) => {
          FileUtils.downloadFile(result, 'Invoice-' + invoiceNumber + '.pdf');
        },
        error: async error => {
          this.invoiceErrorMessage.set("Error downloading invoice: " + await StringUtils.getErrorMessageAsync(error));
        }
      });
  }

  public downloadBol(shipment: PrmsOrderShipment): void {
    const pickNumber = shipment.pickNumber;
    const orderNumber = this.prmsOrderSummary().orderNumber;
    shipment.bolDownloading = true;
    this.errorMessage.set(null);
    this.service.getBOLDocument(orderNumber, pickNumber)
      .pipe(finalize(() => shipment.bolDownloading = false))
      .subscribe({
        next: (blob: Blob) => {
          if (!blob || blob.size === 0) {
            this.errorMessage.set('The Bill of Lading could not be downloaded. Please contact customer service.');
            return;
          }
          FileUtils.downloadFile(blob, `BOL-${orderNumber}_${pickNumber}.pdf`);
        },
        error: () => {
          this.errorMessage.set(
            'Error downloading Bill of Lading. Please contact customer service.'
          );
        }
      });
  }


  public bolExists(shipment: PrmsOrderShipment): void {
    const pickNumber = shipment.pickNumber;
    const orderNumber = this.prmsOrderSummary().orderNumber;
    this.service.getBOLExists(orderNumber, pickNumber).pipe(
      finalize(() => shipment.bolDownloading = false)).subscribe({
        next: (result: boolean) => { shipment.bolExists = result },
      });
  }

}
